import React from 'react';
import { Typography, Avatar } from '@mui/material';
import { ArrowBack, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TranslationEnum, usePortalTranslation } from '../../providers/TranslationProvider';
import './UserProfile.css';

interface UserProfileProps {
  onClose?: () => void;
}

export function UserProfile({ onClose }: UserProfileProps) {
  const navigate = useNavigate();
  const { translate } = usePortalTranslation();
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1); 
    }
  };

  const getProfileData = () => {
    try {
      const authContextString = localStorage.getItem("authContext");
      if (authContextString) {
        const authContext = JSON.parse(authContextString);
        console.log(authContext);
        
        const user = authContext?.user;
        if (user) {
          return {
            name: user.name || "",
            code: user.userAccountId || "",
            email: user.email || "",
            contactNumber: user.mobile || "",
            gstin: user.extendedAttributes?.gstTinNumber || "",
            createdOn: user.creationTime || "",
            supplyChainName: user.supplyChainName || "",
            asmLinked: user.asmLinked || "-",
            address: user.address || ""
          };
        }
      }
    } catch (error) {
      console.error("Error parsing authContext from localStorage:", error);
    }

    return {
      name: "",
      code: "",
      email: "",
      contactNumber: "",
      gstin: "",
      createdOn: "",
      supplyChainName: "",
      asmLinked: "",
      address: ""
    };
  };

  console.log(getProfileData());

  const profileData = getProfileData();

  const maskGSTIN = (gstin: string) => {
    if (!gstin || gstin.length <= 4) return gstin;
    const maskedPart = '*'.repeat(gstin.length - 4);
    const lastFourDigits = gstin.slice(-4);
    return maskedPart + lastFourDigits;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.split(' ')[0];
  };

  const renderAsmLinked = (asmString: string) => {
    if (!asmString || asmString.trim() === "") {
      return (
        <Typography variant="body1" className="user-profile-value">
          -
        </Typography>
      );
    }

    const asmValues = asmString.split(',').map(item => item.trim()).filter(item => item);
    
    if (asmValues.length > 4) {
      return (
        <div
          style={{ position: "relative", display: "inline-block" }}
          tabIndex={0}
        >
          <Typography
            variant="body1"
            className="user-profile-value asm-linked-text"
            style={{ cursor: "pointer" }}
          >
            {asmString}
          </Typography>
          <div
            style={{
              display: "none",
              position: "absolute",
              zIndex: 100,
              background: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              padding: "10px 16px",
              minWidth: 180,
              maxWidth: 300,
              top: "120%",
              left: 0,
              whiteSpace: "normal",
              wordWrap: "break-word"
            }}
            className="asm-linked-tooltip"
          >
            {asmValues.join(", ")}
          </div>
        </div>
      );
    } else {
      return (
        <Typography variant="body1" className="user-profile-value">
          {asmString}
        </Typography>
      );
    }
  };

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        <div className="user-profile-header">
          <div className="user-profile-header-left">
            <ArrowBack 
              className="user-profile-back-icon" 
              onClick={handleClose}
              sx={{
                fontSize: '26px',
              }}
            />
            <Typography variant="h6" className="user-profile-title">
                {translate(TranslationEnum.my_profile, "My Profile")} 
            </Typography>
          </div>
          <Close 
            className="user-profile-close-icon" 
            onClick={handleClose}
            sx={{
              fontSize: '26px',
            }}
          />
        </div>

        <div className="user-profile-content">
          <div className="user-profile-details-grid">
            <div className="user-profile-detail-box user-profile-header-box">
              <div className="user-profile-info-header">
                <Avatar 
                  className="user-profile-avatar"
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    backgroundColor: '#ffe2c9',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    color: '#cf6c1b',
                    border: '0.2px solid #dddddd'
                  }}
                >
                  {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <div className="user-profile-basic-info">
                  <Typography variant="h5" className="user-profile-name">
                    {profileData.name || 'User'}
                  </Typography>
                  <Typography variant="body2" className="user-profile-code">
                    {profileData.code || '-'}
                  </Typography>
                </div>
              </div>
            </div>

            <div className="user-profile-detail-box">
              <div className="user-profile-detail-grid-row">
                <div className="user-profile-detail-row-inline">
                  <Typography variant="body2" className="user-profile-label">
                    {translate(TranslationEnum.my_profile, "Email")}
                  </Typography>
                  <Typography variant="body1" className="user-profile-value">
                    {profileData.email || '-'}
                  </Typography>
                </div>
                <div className="user-profile-detail-row-inline">
                  <Typography variant="body2" className="user-profile-label">
                    {translate(TranslationEnum.my_profile, "GSTIN")}
                  </Typography>
                  <Typography variant="body1" className="user-profile-value">
                    {profileData.gstin ? maskGSTIN(profileData.gstin) : '-'}
                  </Typography>
                </div>
              </div>
              <div className="user-profile-detail-grid-row">
                <div className="user-profile-detail-row-inline">
                  <Typography variant="body2" className="user-profile-label">
                    {translate(TranslationEnum.my_profile, "Contact No.")}
                  </Typography>
                  <Typography variant="body1" className="user-profile-value">
                    {profileData.contactNumber || '-'}
                  </Typography>
                </div>
                <div className="user-profile-detail-row-inline">
                  <Typography variant="body2" className="user-profile-label">
                    {translate(TranslationEnum.my_profile, "Created On")}
                  </Typography>
                  <Typography variant="body1" className="user-profile-value">
                    {formatDate(profileData.createdOn) || '-'}
                  </Typography>
                </div>
              </div>
            </div>

            <div className="user-profile-detail-box">
              <div className="user-profile-detail-grid-row">
                <div className="user-profile-detail-column">
                  <div className="user-profile-detail-row-inline">
                    <Typography variant="body2" className="user-profile-label">
                      {translate(TranslationEnum.my_profile, "Supply Chain Name")}
                    </Typography>
                    <Typography variant="body1" className="user-profile-value">
                      {profileData.supplyChainName || '-'}
                    </Typography>
                  </div>
                  <div className="user-profile-detail-row-inline">
                    <Typography variant="body2" className="user-profile-label">
                      {translate(TranslationEnum.my_profile, "ASM Linked")}
                    </Typography>
                    {renderAsmLinked(profileData.asmLinked)}
                  </div>  
                  <style>
                    {`
                      .asm-linked-tooltip {
                        pointer-events: none;
                        opacity: 0;
                        transition: opacity 0.15s;
                      }
                      [tabindex]:hover .asm-linked-tooltip,
                      [tabindex]:focus .asm-linked-tooltip {
                        display: block !important;
                        pointer-events: auto;
                        opacity: 1;
                      }
                    `}
                  </style>
                </div>
                <div className="user-profile-detail-row-inline">
                  <Typography variant="body2" className="user-profile-label">
                    {translate(TranslationEnum.my_profile, "My Profile")}
                  </Typography>
                  <Typography variant="body1" className="user-profile-value user-profile-address">
                    {profileData.address || '-'}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;